import {
    TableViewStore,
    BaseListViewStore,
    BaasicDropdownStore,
    BasicQueryInputStore,
    BasicQueryCheckboxStore
} from 'core/stores';
import applicationContext from 'core/utils/applicationContext';
import { ProductService, ItemCategoryService, ProcedureCodeService } from 'application/product/services';
import { ItemSubcategoryService } from "application/facility-order/services";
import { GridBooleanCell, GridBackorderedCell } from 'core/components';
import { resolveColumns } from 'application/facility/util/resolveColumns';
import _ from 'lodash';
import { action, observable } from 'mobx';
import React from "react";

@applicationContext
class ProductViewStore extends BaseListViewStore {
    @observable accessor downloadUrl = null;
    constructor(rootStore) {
        const productService = new ProductService(rootStore.application.baasic.apiClient);
        const itemCategoryService = new ItemCategoryService(rootStore.application.baasic.apiClient);
        const procedureCodeService = new ProcedureCodeService(rootStore.application.baasic.apiClient);

        super(rootStore, {
            name: 'product',
            autoInit: true,
            authorization: {
                create: 'productCatalogSection.create',
                delete: 'productCatalogSection.delete',
                update: 'productCatalogSection.update',
                read: 'productCatalogSection.read'
            },
            routes: {
                create: () => this.rootStore.routerStore.goTo(
                    'master.app.main.product.create'
                ),
                edit: id => {
                    if(this.authorization && this.authorization.update){
                        this.setChildNavigationTitle(i => i.id === id, r => r.itemId);
                        return this.rootStore.routerStore.goTo(
                            'master.app.main.product.edit',
                            { id: id }
                        )
                    }
                },
                upload: () => this.rootStore.routerStore.goTo(
                    'master.app.main.product.upload'
                ),
                uploadProductsOrdering: () => this.rootStore.routerStore.goTo(
                    'master.app.main.product.order-upload'
                ),
                preview: item =>{
                    this.setChildNavigationTitle(i => i.id === item.id, item => item.itemId);
                    this.rootStore.routerStore.goTo(
                        'master.app.main.product.preview',
                        { id: item.id }
                    );
                },
            },
            actions: () => {
                return {
                    find: async (params) => {
                        const response = await productService.find(_.extend(params, {
                            embed: 'ItemCategory,UnitOfMeasure,SubCategory,InnerCannula,ProcedureCode_',
                            isActive: params.inactive == undefined || !params.inactive || params.inactive == 'false' ? true : null,
                            showPumps: params.showpumps == undefined || !params.showpumps || params.showpumps == 'false' ? false : true
                        }));
                        const res = _.extend(response.data, {
                            item: _.map(response.data.item, (item => {
                                return _.extend(
                                    item, {
                                    itemCategory: item.itemCategory.name,
                                    uom: item.unitOfMeasure && item.unitOfMeasure.name,
                                    subCategory: item.subCategory && item.subCategory.name,
                                    innerCannula: item.innerCannula && `${item.innerCannula.itemId} ${item.innerCannula.description}`,
                                    procedureCode: item.procedureCode_ && item.procedureCode_.name
                                }
                                );
                            }))
                        })
                        this.onFindSuccess();
                        return res;
                    },
                    delete: async (item) => {
                        await productService.delete(item);
                    }
                }
            }
        });

        this.productService = productService;
        this.itemSubcategoryService = new ItemSubcategoryService(rootStore.application.baasic.apiClient);

        this.setTableStore(new TableViewStore(this.queryUtility, {
            columns: resolveColumns([
                {
                    key: 'itemId',
                    title: 'PRODUCT.LIST.COLUMNS.ITEMID',
                    onClick: item => this.routes.preview(item),
                    width: 150
                },
                {
                    key: 'description',
                    title: 'PRODUCT.LIST.COLUMNS.DESCRIPTION',
                    onClick: item => this.routes.preview(item),
                    width: 300,
                    cell: (cellProps) => {
                        const { dataItem, field } = cellProps;
                        const value = dataItem[field];
                        
                        return (
                            <td 
                                className='c-pointer table--clickable'
                                onClick={() => this.routes.preview(dataItem)}
                            >
                                 {value}
                                 {!cellProps.dataItem.billableToMedicare && <i className="u-icon u-icon--med u u-icon--non-billable u-mar--left--tny" ></i>}   
                            </td>
                        );
                    },
                },
                {
                    key: 'uom',
                    title: 'PRODUCT.LIST.COLUMNS.UOM',
                    onClick: item => this.routes.preview(item),
                    width: 100
                },
                {
                    key: 'backordered',
                    title: 'Backordered',
                    onClick: item => this.routes.preview(item),
                    cell: GridBackorderedCell,
                    width: 100
                },
                {
                    key: 'units',
                    title: 'PRODUCT.LIST.COLUMNS.UNITS',
                    onClick: item => this.routes.preview(item),
                    width: 120
                },
                {
                    key: 'calsPerMl',
                    title: 'PRODUCT.LIST.COLUMNS.CALSPERML',
                    onClick: item => this.routes.preview(item),
                    width: 80
                },
                {
                    key: 'caloriesPerContainer',
                    title: 'PRODUCT.LIST.COLUMNS.CALORIES_PER_CONTAINER',
                    onClick: item => this.routes.preview(item),
                    width: 120
                },
                {
                    key: 'qtyPerCase',
                    title: 'Quantity per Case',
                    onClick: item => this.routes.preview(item),
                    width: 140
                },
                {
                    key: 'billableToMedicare',
                    title: 'Billable to Medicare',
                    onClick: item => this.routes.preview(item),
                    cell: GridBooleanCell,
                    width: 150
                },
                {
                    key: 'mcrallowable',
                    title: 'Medicare Allowable',
                    onClick: item => this.routes.preview(item),
                    width: 160
                },
                {
                    key: 'amountOfUnitsToSend',
                    title: 'Units to Send',
                    onClick: item => this.routes.preview(item),
                    width: 130
                },
                {
                    key: 'procedureCode',
                    title: 'Procedure Code',
                    onClick: item => this.routes.preview(item),
                    width: 140
                },
                {
                    key: 'itemCategory',
                    title: 'PRODUCT.LIST.COLUMNS.ITEM_CATEGORY',
                    onClick: item => this.routes.preview(item),
                    width: 130
                },
                {
                    key: 'subCategory',
                    title: 'PRODUCT.LIST.COLUMNS.SUB_CATEGORY',
                    onClick: item => this.routes.preview(item),
                    width: 150
                },
                {
                    key: 'innerCannula',
                    title: 'PRODUCT.LIST.COLUMNS.INNER_CANNULA',
                    onClick: item => this.routes.preview(item),
                    sort: false,
                    width: 130
                },
                {
                    key: 'isActive',
                    title: 'PRODUCT.LIST.COLUMNS.ACTIVE',
                    onClick: item => this.routes.preview(item),
                    cell: GridBooleanCell,
                    displayFaultyValue: true,
                    width: 100,
                    headerClassName: "center"
                },
            ]),
            actions: {
                onEdit: async (item) => this.routes.edit(item.id),
                onDelete: (item) => this.deleteResource(item),
                onCopy: async (item) => this.copyResource(item),
                onPreview: (item) => this.routes.preview(item)
            }
        }));

        this.itemIdStore = new BasicQueryInputStore();
        this.itemNameStore = new BasicQueryInputStore();
        this.inactiveCheckboxStore = new BasicQueryCheckboxStore();
        this.showPumpsCheckboxStore = new BasicQueryCheckboxStore();

        this.queryUtility.onResetFilter = () => {
            this.itemIdStore.reset();
            this.itemNameStore.reset();
            this.itemCategoryStore.setValue(null);
            this.itemCategoryStore.filterTerm = "";

            this.procedureCodeStore.setValue(null);
            this.procedureCodeStore.filterTerm = "";  

            this.inactiveCheckboxStore.reset();
            this.showPumpsCheckboxStore.reset();
        }

        this.procedureCodeStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await procedureCodeService.find({ searchQuery: term, rpp: 25 });
                return response.data.item;
            }
        });

        this.itemCategoryStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await itemCategoryService.find({ searchQuery: term, rpp: 100 });
                const categoryExceptions = ["enteral---nutrients","enteral---pumps","enteral---supplies"];
                return response.data.item.filter(i => !categoryExceptions.some(c => c === i.abrv));
            }
        });
    }

    @action.bound
    notifySuccessDelete() {
        this.rootStore.notificationStore.success('PRODUCT.LIST.ACTIONS.SUCCESS_DELETE');
    }

    @action.bound
    oninactiveChange(event) {
        const value = event.target.checked;
        this.queryUtility.filter.toggle('inactive');
        this.inactiveCheckboxStore.setValue(value);
    }

    @action.bound
    onShowPumpsChange(event) {
        const value = event.target.checked;
        this.queryUtility.filter.toggle('showpumps');
        this.showPumpsCheckboxStore.setValue(value);
    }

    @action.bound
    async copyResource(item) {
        try {
            const productService = new ProductService(this.rootStore.application.baasic.apiClient);
            const product = await productService.copyProduct(item.id);
            this.routes.edit(product.id);
            this.rootStore.notificationStore.success('PRODUCT.LIST.ACTIONS.SUCCESS_COPY');
        }
        catch (err) {
            this.rootStore.notificationStore.error('PRODUCT.LIST.ACTIONS.ERROR_COPY');
        }
    }
    @action.bound
    onFindSuccess() {
        const parameters = this.queryUtility.plainFilter;
        parameters.inActive = this.queryUtility.filter.inactive == undefined || !this.queryUtility.filter.inactive || this.queryUtility.filter.inactive == 'false' ? false : true,
        parameters.showPumps = this.queryUtility.filter.showpumps == undefined || !this.queryUtility.filter.showpumps || this.queryUtility.filter.showpumps == 'false' ? false : true;
        parameters.sort = "description|asc,itemId|asc";
        this.downloadUrl = `${this.productService.getDownloadUrl(parameters)}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl
            }&ttype=${this.rootStore.authStore.token.type}`;
    }

    @action.bound
    getProductsOrderingDownloadUrl() {
        const parameters = this.queryUtility.plainFilter;
        parameters.sort = "subcategory|asc";
        const downloadProductsOrderingUrl = `${this.itemSubcategoryService.getDownloadUrl({...parameters,  showPumps: parameters.showpumps == undefined || !parameters.showpumps || parameters.showpumps == 'false' ? false : true})}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`
        window.open(downloadProductsOrderingUrl, "_blank");
    }
}

export default ProductViewStore;
